package com.pincer.app

import com.pincer.core.Logger
import com.pincer.core.model.tree.Pincer
import com.pincer.core.model.tree.Participant
import com.pincer.core.model.drafts.PincerDraft
import com.pincer.core.model.drafts.ParticipantDraft
import com.pincer.core.model.drafts.QuestionDraft
import com.pincer.core.functions.validateServerUrl
import com.pincer.core.functions.validateWebSocketUrl
import com.pincer.core.functions.checkPermissionForParticipant
import com.pincer.client.Client
import com.pincer.client.OnChange
import kotlinx.coroutines.CoroutineScope
import com.russhwolf.settings.Settings

class ApplicationState(private val log: Logger, settings: Settings, private val onChange: OnChange) {

    var transient = TransientState()
    val stored = StoredState(log, settings)
    var remote: Client? = null

    fun pincer(): Pincer?                   = remote?.pincer(transient.pid)
    fun pincerUi(): PincerDraft?            = remote?.pincerUi(transient.pid)
    fun pincerSelf(): Participant?          = remote?.pincerSelf(transient.pid)
    fun pincerUiSelf(): ParticipantDraft?   = remote?.pincerUiSelf(transient.pid)

    fun pincerIsReady(): Boolean = pincer() != null
    fun assertPincerIsReady() { if (!pincerIsReady()) throw Exception("Pincer isn't ready") }
    fun isParticipant(): Boolean = pincerSelf() != null
    fun assertParticipant() { if (!isParticipant()) throw Exception("Not a participant") }

    fun personalPincer(): Pincer?           = remote?.personalPincer()
    fun personalUi(): PincerDraft?          = remote?.personalUi()
    fun personalSelf(): Participant?        = remote?.personalSelf()
    fun personalUiSelf(): ParticipantDraft? = remote?.personalUiSelf()

    fun personalIsReady(): Boolean = personalPincer() != null
    fun assertPersonalIsReady() { if (!personalIsReady()) throw Exception("Personal pincer isn't ready") }

    fun locale(): String = personalSelf()?.locale ?: stored.fallbackLocale
    fun zone(): String = personalSelf()?.zone ?: stored.fallbackZone

    fun isOpen(compoundKey: String, atFirst: Boolean): Boolean = transient.opened[compoundKey] ?: atFirst

    fun resetTransientState() {
        log.i("Clearing transient state by replacing object")
        transient = TransientState()
    }

    fun resetStoredState()  {
        log.i("Clearing stored state by clearing settings")
        val serverUrl = stored.serverUrl
        val webSocketUrl = stored.webSocketUrl
        stored.clear()
        if (validateServerUrl(serverUrl) == null && (webSocketUrl.isEmpty() || validateWebSocketUrl(webSocketUrl) == null)) {
            log.i("Keeping server/websocket urls: $serverUrl / $webSocketUrl")
            stored.serverUrl = serverUrl
            stored.webSocketUrl = webSocketUrl
        }
        else {
            log.w("Discarding problematic server/websocket urls on reset $serverUrl / $webSocketUrl")
        }
    }

    suspend fun resetRemoteState(coroutineScope: CoroutineScope) {
        if (remote != null) {
            log.i("Stopping old client before starting new one")
            remote!!.stop()
        }
        log.i("Setting up new client")
        val client = Client.build(
            log = log,
            coroutineScope = coroutineScope,
            serverUrl = stored.serverUrl,
            webSocketUrl = stored.webSocketUrl,
            deviceId = stored.deviceId,
            deviceSecret = stored.deviceSecret,
        )
        if (stored.deviceId == null) {
            log.i("New device id: ${client.deviceId}")
            stored.deviceId = client.deviceId
        }
        stored.deviceSecret = client.deviceSecret
        client.onDeviceSecretChange = { deviceSecret ->
            log.i("Updating device secret ")
            log.i("Old: ${stored.deviceSecret?.substring(14)}*******")
            stored.deviceSecret = deviceSecret
            log.i("New: ${stored.deviceSecret?.substring(14)}*******")
        }
        client.listenToChanges(onChange)
        remote = client
    }

    fun check(scope: String, write: Boolean)
        = checkPermissionForParticipant(pincer = pincer(), scope = scope, write = write, principalId = remote?.principalId)
    fun checkRead(scope: String) = check(scope, false)
    fun checkWrite(scope: String) = check(scope, true)

}
